/**
 * Domain field option utilities.
 *
 * Options on a domain field can arrive in two shapes:
 *   - legacy: Array<string>  e.g. ['Red', 'Green', 'Blue']
 *   - canonical: Array<{ value, label, color }>
 *
 * The canonical shape is what every UI consumer should see. These helpers
 * normalise on both read (so legacy configs render) and write (so new configs
 * never persist the legacy shape). Without them, every <select> over
 * field.options has to guess and a single wrong guess crashes React (#31).
 */

const DEFAULT_COLOR = 'neutral';

/**
 * Slugify a label into a stable option value. Falls back to `opt_N` for empty
 * input so callers always get a non-empty value.
 */
function slugify(label, index = 0) {
  const base = String(label ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return base || `opt_${index}`;
}

/**
 * Normalise a single option to canonical shape.
 * @param {string|object} opt
 * @param {number} index - position in the array, used for fallback values
 */
export function normaliseOption(opt, index = 0) {
  if (opt == null) return null;
  if (typeof opt === 'string') {
    const value = slugify(opt, index);
    return { value, label: opt, color: DEFAULT_COLOR };
  }
  if (typeof opt === 'object') {
    const label = opt.label ?? opt.value ?? '';
    const value = opt.value ?? slugify(label, index);
    const color = opt.color || DEFAULT_COLOR;
    // Availability belongs to the shared choice definition. A label/colour
    // edit or a runtime read must not turn a restricted choice into Whole school.
    return { value, label, color,
      ...(opt.phaseApplicability !== undefined ? { phaseApplicability: opt.phaseApplicability } : {}),
    };
  }
  return null;
}

/**
 * Normalise an options array. Non-arrays become []. Null/undefined entries
 * are dropped.
 */
export function normaliseOptions(options) {
  if (!Array.isArray(options)) return [];
  return options.map((o, i) => normaliseOption(o, i)).filter(Boolean);
}

/**
 * Reconcile the builder's friendly type names with the engine's canonical
 * vocabulary (the set `schemaAssert` enforces). The builder historically wrote
 * `binary` for a yes/no field; the renderer, schema, and every protected
 * config use `boolean`. This is the single chokepoint that closes that fork —
 * every field returned by `normaliseFields` carries a canonical type, so a
 * legacy `binary` field renders and validates identically to a `boolean` one.
 *
 * `text`/`string` are both canonical (long vs short text) and pass through.
 * @param {string} type
 * @returns {string}
 */
export function normaliseType(type) {
  if (type === 'binary') return 'boolean';
  return type;
}

/**
 * Coerce a value at the boundary where a Domain field becomes event data.
 * Keeping this beside the field/option normalisers gives browser forms,
 * imports, and other writers one typed representation for the same schema.
 *
 * `preserveTextWhitespace` is intended for controlled text inputs while the
 * person is still typing. Persistence/import callers retain the default trim.
 */
export function normaliseFieldValue(field, raw, { preserveTextWhitespace = false } = {}) {
  if (raw === null || raw === undefined) return null;

  const type = normaliseType(field?.type);
  if (type === 'number' || type === 'rating' || type === 'star') {
    if (raw === '') return null;
    const value = Number(raw);
    if (!Number.isFinite(value)) {
      throw new Error(`${field?.label || 'Value'} must be a number`);
    }
    return value;
  }

  if (type === 'boolean') {
    if (typeof raw === 'boolean') return raw;
    const normalized = String(raw).trim().toLowerCase();
    if (['true', 'yes', '1', 'y'].includes(normalized)) return true;
    if (['false', 'no', '0', 'n', ''].includes(normalized)) return false;
    throw new Error(`${field?.label || 'Value'} must be Yes or No`);
  }

  if (type === 'multiChoice') {
    if (raw === '') return null;
    const normalized = String(raw).trim().toLowerCase();
    const option = normaliseOptions(field?.options).find((item) => (
      String(item.value).toLowerCase() === normalized
      || String(item.label).toLowerCase() === normalized
    ));
    if (!option) throw new Error(`${raw} is not a valid ${field?.label || 'choice'}`);
    return option.value;
  }

  if (type === 'string' || type === 'text') {
    if (typeof raw !== 'string') return raw;
    return preserveTextWhitespace ? raw : raw.trim();
  }

  if (typeof raw === 'string') {
    return preserveTextWhitespace ? raw : raw.trim();
  }
  return raw;
}

/**
 * Normalise every field in a field array: canonicalise its `type` and (when
 * present) its `options`. Fields without options keep their other props
 * untouched; only `type` and `options` are ever rewritten.
 */
export function normaliseFields(fields) {
  if (!Array.isArray(fields)) return [];
  return fields.map((f) => {
    if (!f || typeof f !== 'object') return f;
    const next = { ...f };
    if (f.type) next.type = normaliseType(f.type);
    if (Array.isArray(f.options)) next.options = normaliseOptions(f.options);
    return next;
  });
}
